import React, { useEffect, useRef, useState } from "react";
import styles from "./RepairService.module.scss";

type Step = { title: string; text: string };
type Mode = { id: string; label: string; steps: Step[]; imageSrc: string };

const RepairService: React.FC = () => {
  // Дані режимів (усередині компонента)
  const modes: Mode[] = [
    {
      id: "post",
      label: "per Post",
      imageSrc: "/images/EU/delivery-img.jpg",
      steps: [
        {
          title: "Beratung",
          text: "Kontaktieren Sie uns für eine individuelle Beratung zu Ihrem Anliegen.",
        },
        {
          title: "Versand",
          text: "Senden Sie uns das defekte Bauteil – auf Wunsch organisieren wir die Abholung und den Rückversand für Sie.",
        },
        {
          title: "Kostenlose Diagnose",
          text: "Wir prüfen das Bauteil und teilen Ihnen die Ergebnisse sowie die Reparaturkosten mit.",
        },
        {
          title: "Reparaturfreigabe",
          text: "Nach Ihrer Bestätigung und Bezahlung beginnen wir umgehend mit der Reparatur. Die Reparatur dauert in der Regel 1–2 Werktage.",
        },
        {
          title: "Rückversand",
          text: "Das reparierte Bauteil wird sicher verpackt und mit Sendungsverfolgung an Sie zurückgeschickt.",
        },
      ],
    },
    {
      id: "onsite",
      label: "vor Ort",
      imageSrc: "/images/EU/delivery-img2.jpeg",
      steps: [
        {
          title: "Termin & Ankunft",
          text: "Der Kunde meldet sich telefonisch oder kommt direkt zu uns – mit dem Bauteil oder mit dem ganzen Fahrzeug.",
        },
        {
          title: "Übergabe",
          text: "Wir übernehmen die defekte Einheit oder das Fahrzeug und erfassen alle relevanten Daten.",
        },
        {
          title: "Diagnose vor Ort",
          text: "Unsere Techniker prüfen das Bauteil oder das Fahrzeug und ermitteln die Ursache.",
        },

        {
          title: "Angebot: Reparatur oder Austausch",
          text: "Wir erstellen ein verbindliches Angebot mit Preis, Dauer und Optionen (Instandsetzung oder Austausch).",
        },
        {
          title: "Freigabe & Zahlung",
          text: "Nach Ihrer Zustimmung und Bezahlung beginnen wir sofort mit der Reparatur oder dem Austausch.",
        },
        {
          title: "Abholung mit Garantie",
          text: "Der Kunde erhält das reparierte Bauteil oder das instandgesetzte Fahrzeug zurück – mit Garantie.",
        },
      ],
    },
  ];

  const [active, setActive] = useState(0);
  const count = modes.length;
  const activeMode = modes[active];
  const pad2 = (n: number) => n.toString().padStart(2, "0");

  // refs для синхронізації висот
  const stepsRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // виставляємо --left-h = висота лівої колонки
  useEffect(() => {
    if (!stepsRef.current || !containerRef.current) return;

    const update = () => {
      const h = stepsRef.current!.offsetHeight;
      containerRef.current!.style.setProperty("--left-h", `${h}px`);
    };

    const ro = new ResizeObserver(update);
    ro.observe(stepsRef.current);

    update();
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [active]); // при зміні режиму теж переміряємо

  return (
    <div className="container section">
      <div
        className={`${styles.repairService} sectionContainer sectionContainerWhite bigSection`}
      >
        <div className={styles.bgDarkOverlayPostversand}></div>
        <div className={`sideP64 titleCont ${styles.mainTitleContainer}`}>
          <div className="titleBlock">
            <h2>So funktioniert unser Reparaturservice für Sie</h2>
          </div>

          <div className={styles.segmented}>
            <div
              className={styles.segTrack}
              style={
                {
                  // @ts-ignore — CSS custom properties
                  "--count": count,
                  // @ts-ignore
                  "--index": active,
                } as React.CSSProperties
              }
              role="tablist"
              aria-label="Service modes"
            >
              <div className={styles.segSlider} aria-hidden="true" />
              {modes.map((m, i) => (
                <button
                  key={m.id}
                  role="tab"
                  aria-selected={i === active}
                  className={`${styles.segOption} ${
                    i === active ? styles.isActive : ""
                  }`}
                  onClick={() => setActive(i)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          className={`${styles.repairServiceContainer} sideP64`}
          ref={containerRef}
        >
          <div className={`${styles.stepsContainerBig}`} ref={stepsRef}>
            {activeMode.steps.map((step, index) => (
              <div key={index} className={styles.step}>
                <div className={styles.stepNumber}>{pad2(index + 1)}</div>
                <div className={styles.pointBlock}>
                  <div className={styles.point}></div>
                </div>
                {index < activeMode.steps.length - 1 && (
                  <div className={styles.stepline}></div>
                )}
                <div className={styles.stepContent}>
                  <h2 className={styles.stepTitle}>{step.title}</h2>
                  <p className={styles.stepText}>{step.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.imageCol}>
            <img
              src={activeMode.imageSrc}
              alt="Reparatur-Service"
              className={styles.headerImage}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairService;
